import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { storage } from "@/lib/api";
import { QUESTIONS, QUIZ_PROGRESS_KEY } from "@/lib/quizConfig";
import QuizTransition, { TRANSITIONS } from "@/components/QuizTransition";

export default function Quiz() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const [transitionIdx, setTransitionIdx] = useState(null);

  const q = QUESTIONS[index];
  const total = QUESTIONS.length;
  const progressPct = (index / total) * 100;

  useEffect(() => {
    const raw = storage.get(QUIZ_PROGRESS_KEY);
    if (raw) {
      try {
        const p = JSON.parse(raw);
        if (p.answers) setAnswers(p.answers);
        if (typeof p.index === "number" && p.index < QUESTIONS.length) {
          setIndex(p.index);
        }
      } catch {
        /* noop */
      }
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    storage.set(QUIZ_PROGRESS_KEY, JSON.stringify({ index, answers }));
  }, [index, answers, hydrated]);

  const selectOption = (value) => {
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);
    setTimeout(() => {
      // Show transition page after Q9 (idx 8), Q12 (11), Q15 (14), Q19 (18)
      if (TRANSITIONS[index]) {
        setTransitionIdx(index);
        if (typeof window !== "undefined") window.scrollTo({ top: 0 });
        return;
      }
      if (index < QUESTIONS.length - 1) {
        setIndex(index + 1);
      } else {
        navigate("/lead-capture");
      }
    }, 220);
  };

  const continueFromTransition = () => {
    const finishedIdx = transitionIdx;
    setTransitionIdx(null);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
    if (finishedIdx === QUESTIONS.length - 1) {
      navigate("/lead-capture");
    } else {
      setIndex(finishedIdx + 1);
    }
  };

  const back = () => {
    if (transitionIdx !== null) {
      setTransitionIdx(null);
      return;
    }
    if (index > 0) setIndex(index - 1);
    else navigate(-1);
  };

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-connexus">
        Loading…
      </div>
    );
  }

  if (transitionIdx !== null && TRANSITIONS[transitionIdx]) {
    return (
      <QuizTransition
        data={TRANSITIONS[transitionIdx]}
        onContinue={continueFromTransition}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-5 px-4 pb-2 flex items-center">
        <button
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center text-connexus hover:bg-connexus-accent transition-colors"
          data-testid="quiz-back-button"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="px-5 pb-3">
        <div
          className="w-full h-1.5 bg-connexus-accent rounded-full overflow-hidden"
          data-testid="quiz-progress-bar"
        >
          <div
            className="h-full bg-connexus transition-all duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="mt-2.5 text-[11px] uppercase tracking-[0.14em] text-connexus-textMuted font-semibold">
          Question {index + 1} of {total}
        </p>
      </div>

      <div className="px-6 pt-2 pb-12 max-w-2xl mx-auto fade-up" key={q.id}>
        <h2
          className="font-heading text-3xl sm:text-4xl leading-tight tracking-tight text-connexus mb-8"
          data-testid="quiz-question-prompt"
        >
          {q.prompt}
        </h2>

        <div className="flex flex-col gap-3.5">
          {q.options.map((opt, i) => {
            const selected = answers[q.id] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => selectOption(opt.value)}
                className={`flex items-center gap-3.5 text-left border-2 rounded-2xl p-4 sm:p-5 transition-all active:scale-[0.99] ${
                  selected
                    ? "border-connexus bg-connexus-accent shadow-connexus-soft"
                    : "border-connexus-border bg-white hover:border-connexus-muted hover:bg-connexus-surface"
                }`}
                data-testid={`quiz-option-${i}`}
              >
                <span
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    selected
                      ? "border-connexus"
                      : "border-connexus-borderStrong"
                  }`}
                >
                  {selected && (
                    <span className="w-2.5 h-2.5 rounded-full bg-connexus" />
                  )}
                </span>
                <span
                  className={`text-base leading-relaxed ${
                    selected
                      ? "text-connexus font-semibold"
                      : "text-black font-medium"
                  }`}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
